import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageHeader, EmptyState } from "../components/Section";
import { Play, Music } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogHeader } from "../components/ui/dialog";

function extractYouTubeId(url) {
  if (!url) return "";
  const m = url.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : "";
}

export default function Sermons() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/sermons").then((r) => setItems(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="sermons-page">
      <PageHeader
        eyebrow="TEACHING & PROCLAMATION"
        title="Sermons"
        subtitle="Watch and listen to messages preached in the Council."
        bg="https://images.unsplash.com/photo-1519491050282-cf00c82424b4?crop=entropy&cs=srgb&fm=jpg&w=1800&q=80"
      />
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {items.length === 0 ? (
            <EmptyState title="No sermons yet" hint="Check back soon — teaching is posted each week." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((s) => {
                const yid = extractYouTubeId(s.youtube_url);
                return (
                  <Dialog key={s.id}>
                    <DialogTrigger asChild>
                      <button data-testid={`sermon-card-${s.id}`} className="text-left group border hairline rounded-xl overflow-hidden surface hover:border-gold/50 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold">
                        <div className="aspect-video relative overflow-hidden bg-black">
                          {yid ? (
                            <img src={`https://img.youtube.com/vi/${yid}/hqdefault.jpg`} alt={s.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                          ) : <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-black"><Music className="w-10 h-10 text-gold/60" /></div>}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="w-14 h-14 rounded-full bg-black/60 backdrop-blur-md border border-gold/70 flex items-center justify-center group-hover:bg-gold group-hover:text-black transition-colors duration-200">
                              <Play className="w-5 h-5 text-gold group-hover:text-black" />
                            </span>
                          </div>
                        </div>
                        <div className="p-6">
                          <div className="font-accent text-[10px] text-gold mb-3">{s.date} · {s.scripture}</div>
                          <h3 className="font-display text-xl text-white">{s.title}</h3>
                          <p className="mt-1 text-sm text-zinc-400">{s.speaker}</p>
                          <p className="mt-3 text-sm text-zinc-500 line-clamp-2">{s.description}</p>
                        </div>
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl bg-[#0a1930] border-zinc-800">
                      <DialogHeader>
                        <DialogTitle className="font-display text-white">{s.title}</DialogTitle>
                      </DialogHeader>
                      {yid ? (
                        <div className="aspect-video">
                          <iframe title={s.title} className="w-full h-full rounded" src={`https://www.youtube.com/embed/${yid}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                        </div>
                      ) : s.audio_url ? (
                        <audio controls src={s.audio_url} className="w-full" />
                      ) : (
                        <p className="text-zinc-400">No media attached.</p>
                      )}
                      <p className="text-sm text-zinc-400">{s.description}</p>
                      <p className="text-xs text-zinc-500">{s.speaker} · {s.scripture} · {s.date}</p>
                    </DialogContent>
                  </Dialog>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
